"use client";

import { useMemo } from "react";

import { useSession } from "@/app/hooks/useSession";
import { PageHeader } from "@/components/shared/PageHeader";
import type { DashboardEntry } from "@/lib/dashboard/server";

import { ComingUpWeek } from "./ComingUpWeek";
import { EmptyDashboard } from "./EmptyDashboard";
import { PrepAhead } from "./PrepAhead";
import { ShoppingCheck } from "./ShoppingCheck";
import { TodayCooking } from "./TodayCooking";
import { WeeklyDraft } from "./WeeklyDraft";

interface DashboardSurfaceProps {
  entries: DashboardEntry[];
  todayIso: string;
}

export function DashboardSurface({ entries, todayIso }: DashboardSurfaceProps) {
  const todayEntries = useMemo(
    () => entries.filter((e) => e.cookOn === todayIso),
    [entries, todayIso]
  );

  const isEmpty = entries.length === 0;
  const { isAuthenticated, isLoading: sessionLoading } = useSession();

  return (
    <>
      <PageHeader />
      <main className="flex min-h-[calc(100dvh-var(--navbar-height))] md:min-h-dvh flex-col">
        {isEmpty ? (
          // Nothing planned: signed in, this week's free suggested plan (or the empty state when there is none);
          // signed out, the empty state with no request.
          isAuthenticated ? (
            <WeeklyDraft />
          ) : sessionLoading ? null : (
            <EmptyDashboard />
          )
        ) : (
          <div className="mx-auto w-full max-w-wide-content space-y-8 px-3 py-4 md:py-8">
            {todayEntries.length > 0 && <TodayCooking entries={todayEntries} />}

            <ComingUpWeek entries={entries} todayIso={todayIso} />

            <div className="flex flex-col gap-8 @md:flex-row @md:items-start">
              <ShoppingCheck weekEntries={entries} />
              <div className="min-w-0 flex-1">
                <PrepAhead recipeCount={entries.length} entries={entries} />
              </div>
            </div>
          </div>
        )}
      </main>
    </>
  );
}
